/**
 * ノード上のファイルパス契約（ARCHITECTURE.md §4.8 が唯一の正）。
 *
 * コントロールプレーン・エージェント・Dovecot の `user_query`・Postfix の
 * `virtual_mailbox_maps` の四者が同じ計算をしないと配送先が食い違う。
 * パスを文字列で組み立てる箇所は必ずこのモジュールを経由すること。
 *
 * 設計書 01 の `/var/vmail`、03 の `/var/mail/vhosts` は**廃止**。
 */

/* ------------------------------------------------------------ ルート定数 */

/** Maildir のルート。Dovecot の `mail_location` もこの下を指す。 */
export const MAILDIR_ROOT = '/srv/vmail';

/** 管理 Sieve（`sieve_before` で無条件先行実行されるスクリプト）のルート。 */
export const MANAGED_SIEVE_ROOT = '/srv/mailsv-sieve';

/** Dovecot のインデックス（Maildir 本体とは別ボリュームに置いて I/O を分ける）。 */
export const MAIL_INDEX_ROOT = '/srv/vmail-index';

/** 設定ツリーのルート。`current` は世代ディレクトリへのシンボリックリンク。 */
export const CONF_ROOT = '/etc/mailsv';
export const CONF_CURRENT = `${CONF_ROOT}/current`;
export const CONF_GENERATIONS = `${CONF_ROOT}/generations`;
/** 保持する設定世代数。これを超えた古い世代はエージェントが削除する。 */
export const CONF_GENERATIONS_KEEP = 5;

/** CP から配布される秘密（SRS 等）の置き場。 */
export const SECRETS_DIR = `${CONF_ROOT}/secrets`;
export const SRS_KEY_PATH = `${SECRETS_DIR}/srs.key`;

/** rspamd が読む DKIM 秘密鍵ツリー。 */
export const DKIM_ROOT = '/var/lib/rspamd/dkim';
export const DKIM_SELECTOR_MAP = `${DKIM_ROOT}/selectors.map`;
/**
 * from_acl.lua が参照する「認証ユーザー ↔ 送信可能ドメイン」の突合マップ。
 *
 * **`/var/lib/rspamd` 直下ではない。** 共有ボリューム（§4.10 の `mailnode_dkim`）
 * が掛かっているのは `/var/lib/rspamd/dkim` だけで、その 1 つ上は rspamd
 * コンテナのローカル FS なのでエージェントからは物理的に書けない。
 * `stack/mailnode/config/rspamd/lua/from_acl.lua` の `url` と同じ値であること。
 */
export const FROM_ACL_MAP = `${DKIM_ROOT}/from_acl.map`;

/** ログ。stdout ではなくファイルに出し、エージェントが tail する。 */
export const LOG_ROOT = '/var/log/mailsv';
export const LOG_FILES = {
  postfix: `${LOG_ROOT}/postfix.log`,
  dovecot: `${LOG_ROOT}/dovecot.log`,
  rspamd: `${LOG_ROOT}/rspamd.log`,
} as const;

/**
 * VERP バウンスの取り込み結果（1 行 JSON）。
 *
 * Postfix の `bounce-ingest` パイプが**書き**、エージェントが**読む**。
 * `LOG_FILES` に入れないのは、こちらがサービスのログではなく
 * プロセス間の受け渡しであり、ローテーション時の `logreopen` の
 * 対象にならない（書き手がパイプで毎回開き直す）ため。
 */
export const BOUNCE_INGEST_LOG = `${LOG_ROOT}/bounce-ingest.log`;

/**
 * FBL（苦情フィードバックループ / ARF）の取り込み結果（1 行 JSON）。
 *
 * Postfix の `fbl-ingest` パイプが**書き**、エージェントが**読む**。
 * 中身は `message/feedback-report` パートの許可した見出し行だけで、
 * 元メール（`message/rfc822` パート）は 1 バイトも入らない。
 */
export const FBL_INGEST_LOG = `${LOG_ROOT}/fbl-ingest.log`;

/** Postfix のスプール（エージェントからは ro でマウントし queue 統計だけ読む）。 */
export const POSTFIX_SPOOL = '/var/spool/postfix';

/** 特権ヘルパ（shim / netguard / updater）のソケット置き場。 */
export const CTL_DIR = '/run/mailsv/ctl';
/**
 * netguard（nftables を操作する特権ヘルパ）のソケット。
 *
 * **`/run/mailsv/netguard.sock` ではない。** shim と同じ `ctl` ボリューム
 * （`CTL_DIR`）の中に居る。1 つ上の `/run/mailsv` はコンテナごとのローカル FS
 * なので、そこにソケットを置いてもエージェントからは見えない。
 * `apps/agent/src/config.ts` の既定（`${MAILSV_CTL_DIR}/netguard.sock`）と
 * `stack/mailnode/docker-compose.yml` の `MAILSV_NETGUARD_SOCK` が実配置の正で、
 * この定数はそれに揃えてある（A119。以前は 3 者が食い違っていた）。
 */
export const NETGUARD_SOCK = `${CTL_DIR}/netguard.sock`;

/**
 * updater（ホストの systemd 側で動く更新ヘルパ）のソケット。
 *
 * **`ctl` ボリュームの中ではない。** updater はコンテナではなくホストで動き、
 * `mailnode_ctl` には一切書き込まない。接点は compose の
 * `- /run/mailsv/updater:/run/mailsv/updater` という bind mount 1 本だけ。
 * `CTL_DIR` 配下を指すと**永遠にソケットが見つからず、自己更新が黙って
 * 無効になる**（`updater.ts` の `probe()` が false を返し続ける）。
 */
export const UPDATER_SOCK = '/run/mailsv/updater/updater.sock';

/** エージェント自身の状態ディレクトリ。 */
export const AGENT_DATA_DIR = '/var/lib/mailsv-agent';
export const AGENT_PATHS = {
  /** Ed25519 秘密鍵（0600 + systemd-creds 封緘）。CP へは絶対に送らない。 */
  nodeKey: `${AGENT_DATA_DIR}/keys/node_ed25519.jwk`,
  identity: `${AGENT_DATA_DIR}/identity.json`,
  /** last-good な desired-state / secrets のディスクキャッシュ（CP 停止中の可用性担保）。 */
  stateDir: `${AGENT_DATA_DIR}/state`,
  /** CP へ送れなかった status/metrics/usage/audit の永続バッファ。 */
  bufferDir: `${AGENT_DATA_DIR}/buffer`,
  tailOffsets: `${AGENT_DATA_DIR}/tail-offsets.json`,
  acmeAccount: `${AGENT_DATA_DIR}/acme/account.jwk`,
} as const;

/* ------------------------------------------------------ パス組み立て関数 */

/**
 * パス部品として安全か検証する。
 * `..` / `/` / 制御文字 / 空文字 を弾く。ここを通さずに文字列連結すると
 * ディレクトリトラバーサルが Maildir の外へ抜ける。
 */
export function assertSafePathSegment(segment: string, label = 'パス部品'): void {
  if (segment.length === 0) {
    throw new Error(`${label}が空です`);
  }
  if (segment === '.' || segment === '..') {
    throw new Error(`${label}に "${segment}" は使えません`);
  }
  if (/[/\\\0]/.test(segment)) {
    throw new Error(`${label}にパス区切り文字は含められません: ${segment}`);
  }
  // 前後の空白やドット始まりは Maildir を隠しディレクトリ化してしまう。
  if (segment.startsWith('.') || segment !== segment.trim()) {
    throw new Error(`${label}の先頭のドットと前後の空白は許可されません: ${segment}`);
  }
}

/**
 * desired-state に載せる **相対** Maildir パス（§4.8）。
 * エージェントが `MAILDIR_ROOT` を前置して絶対パスにする。
 * 末尾のスラッシュは Maildir 形式であることを示す Postfix / Dovecot の慣習。
 */
export function maildirPath(domain: string, localPart: string): string {
  assertSafePathSegment(domain, 'ドメイン名');
  assertSafePathSegment(localPart, 'ローカルパート');
  return `${domain}/${localPart}/`;
}

/**
 * ノード DB `mailboxes.maildir` に入れる **絶対** パス（§4.8）。
 * `virtual_mailbox_maps` がそのまま返す値なので、末尾スラッシュ必須。
 */
export function absoluteMaildirPath(domain: string, localPart: string): string {
  return `${MAILDIR_ROOT}/${maildirPath(domain, localPart)}Maildir/`;
}

/** Dovecot の `home`（Maildir の 1 つ上。末尾スラッシュなし）。 */
export function mailboxHomePath(domain: string, localPart: string): string {
  assertSafePathSegment(domain, 'ドメイン名');
  assertSafePathSegment(localPart, 'ローカルパート');
  return `${MAILDIR_ROOT}/${domain}/${localPart}`;
}

/**
 * 管理 Sieve スクリプトの絶対パス。
 *
 * **`root` を受け取るのは、エージェント側で「書く先」と「消す先」が
 * 別々のパスを見ていたため**（2026-08-30）。`syncSieve()` は
 * `MAILSV_SIEVE_DIR`（設定可能）配下を走査して desired-state に無いものを
 * `rm -rf` する一方、書き込み先はここが返す `MANAGED_SIEVE_ROOT` に
 * 固定されていた。既定値では両者が一致するので**既定のままなら動く**が、
 * `MAILSV_SIEVE_DIR` を既定以外にした瞬間、
 *
 *   - 書き込みは `/srv/mailsv-sieve/…` へ
 *   - 削除判定は設定されたディレクトリを走査
 *
 * となり、走査側の項目は**絶対に `keep` に入らない**ため
 * **その配下の 2 階層目を全部消す**。設定を 1 つ変えただけで
 * 契約者の自動応答・転送が全部消える形だった。
 *
 * 引数で受けるようにして、書く先と消す先が構造的に一致するようにした。
 */
export function managedSievePath(domain: string, localPart: string, root: string = MANAGED_SIEVE_ROOT): string {
  assertSafePathSegment(domain, 'ドメイン名');
  assertSafePathSegment(localPart, 'ローカルパート');
  return `${root}/${domain}/${localPart}/managed.sieve`;
}

/** DKIM 秘密鍵の絶対パス（0400 `_rspamd`）。 */
export function dkimKeyPath(domain: string, selector: string): string {
  assertSafePathSegment(domain, 'ドメイン名');
  assertSafePathSegment(selector, 'DKIM セレクタ');
  return `${DKIM_ROOT}/${domain}/${selector}.key`;
}

/** 証明書ファイルの配置（`fullchain.pem` 0644 / `privkey.pem` 0640 root:5000）。 */
export function certPaths(name: string): { dir: string; fullchain: string; privkey: string } {
  assertSafePathSegment(name, '証明書名');
  const dir = `${CONF_CURRENT}/tls/${name}`;
  return { dir, fullchain: `${dir}/fullchain.pem`, privkey: `${dir}/privkey.pem` };
}

/** 設定世代ディレクトリ。 */
export function generationDir(generation: number | string): string {
  const g = String(generation);
  if (!/^\d+$/.test(g)) throw new Error(`世代番号が不正です: ${g}`);
  return `${CONF_GENERATIONS}/${g}`;
}

/** ファイルモード（8 進）。エージェントが `chmod` するときの唯一の正。 */
export const FILE_MODES = {
  /** 公開してよい証明書チェーン */
  certFullchain: 0o644,
  /** 秘密鍵。root 所有 + vmail グループ読み取り */
  certPrivkey: 0o640,
  /**
   * DKIM 秘密鍵。
   *
   * **0400 ではなく 0440。** 0400 は「所有者だけが読める」であり、
   * 所有者はこれを書いたエージェント（uid 5001）になる。エージェントは
   * **非 root なので `chown` で所有者を rspamd(5004) へ移せない**
   * （`chown(2)` の所有者変更は CAP_CHOWN が要る）。0400 のままだと
   * rspamd から鍵が読めず、**DKIM 署名が黙って付かない**
   * （配送そのものは成功するので、受信側の DMARC が落ちるまで気づけない）。
   *
   * そこでグループ読み取りを開き、鍵ディレクトリ側を setgid（2750
   * `mailsv:mnrspamd`）にして**新規ファイルのグループが自動的に 5004 になる**
   * ようにしてある（`DKIM_DIR_MODE`）。所有者変更は要らず、
   * 読めるのは rspamd グループだけ、という状態を非 root のまま作れる。
   */
  dkimKey: 0o440,
  /** エージェントの Ed25519 秘密鍵 */
  agentKey: 0o600,
  /** SRS 秘密 */
  srsKey: 0o640,
  /** 生成した設定ファイル */
  configFile: 0o644,
  configDir: 0o755,
} as const;

/**
 * DKIM 鍵ディレクトリのモード。**setgid（2000）が本体**。
 *
 * `/var/lib/rspamd/dkim` とその配下を `2750 mailsv(5001):mnrspamd(5004)` に
 * しておくと、非 root のエージェントが作ったファイルのグループが自動的に
 * 5004 になる。`FILE_MODES.dkimKey`（0440）と組で初めて
 * 「rspamd だけが読める鍵を非 root で配置する」が成立する。
 *
 * イメージ側（`stack/mailnode/images/rspamd/Dockerfile` と
 * `docker/node.Dockerfile`）の `mkdir` もこの所有者・モードに揃えること。
 * named volume は**最初にマウントしたコンテナのイメージ上の所有者を引き継ぐ**
 * ので、片方だけ直しても起動順によっては root 所有のままになる。
 */
export const DKIM_DIR_MODE = 0o2750;
